import * as Flex from "@twilio/flex-ui";
import * as features from "../../feature-library";
import { FeatureDefinition } from "../../types/feature-loader";
import * as Actions from "./actions";
import * as Channels from "./channels";
import * as ChatOrchestrator from "./chat-orchestrator";
import * as Components from "./components";
import * as CssOverrides from "./css-overrides";
import * as Events from "./events";
import * as JsClientEvents from "./jsclient-event-listeners";
import * as NotificationEvents from "./notification-events";
import * as Notifications from "./notifications";
import * as PasteElements from "./paste-elements";
import * as Strings from "./strings";
import * as TeamsFilters from "./teams-filters";

const loadFeature = (flex: typeof Flex, manager: Flex.Manager, feature: FeatureDefinition) => {
  // Features invoke this function to register their hooks
  const name = feature.name ?? "unknown";
  const hooks = feature.hooks ?? [];
  const actions = feature.actions ?? [];
  console.info(`%cFeature enabled: ${name}`, "background: green; color: white;");

  for (const hook of hooks) {
    /*
     * Each hook exports a function or property for us to handle.
     * Register the hook based on the export(s) present.
     */

    if (hook.actionHook) {
      Actions.addHook(flex, manager, name, hook);
    }

    if (hook.channelHook) {
      Channels.addHook(flex, manager, name, hook);
    }

    if (hook.chatOrchestratorHook) {
      ChatOrchestrator.addHook(flex, manager, name, hook);
    }

    if (hook.componentHook) {
      Components.addHook(flex, manager, name, hook);
    }

    if (hook.cssOverrideHook) {
      CssOverrides.addHook(flex, manager, name, hook);
    }

    if (hook.eventHook) {
      Events.addHook(flex, manager, name, hook);
    }

    if (hook.jsClientHook) {
      JsClientEvents.addHook(flex, manager, name, hook);
    }

    if (hook.notificationHook) {
      Notifications.addHook(flex, manager, name, hook);
    }

    if (hook.notificationEventHook) {
      NotificationEvents.addHook(flex, manager, name, hook);
    }

    if (hook.pasteElementHook) {
      PasteElements.addHook(flex, manager, name, hook);
    }

    if (hook.stringHook) {
      Strings.addHook(flex, manager, name, hook);
    }

    if (hook.teamsFilterHook) {
      TeamsFilters.addHook(flex, manager, name, hook);
    }
  }

  for (const action of actions) {
    Flex.Actions.registerAction(action.name, async (payload: any) => action.handle(payload));
  }
};

export const initFeatures = (flex: typeof Flex, manager: Flex.Manager) => {
  if (typeof features === "undefined") {
    // no features discovered; abort
    return;
  }

  for (const file of Object.values(features)) {
    // Each feature index file should export a `register` function for us to invoke
    if (!file.register) {
      console.error("Feature found, but its index file does not export a `register` function", file);
      return;
    }

    try {
      const feature = file.register() as FeatureDefinition;

      if (feature && feature.name) {
        loadFeature(flex, manager, feature);
      }
    } catch (error) {
      console.error("Error loading feature:", error);
    }
  }

  // After all features have initialized, execute deferred hooks
  CssOverrides.init(manager);
  PasteElements.init(flex);
  Strings.init(manager);
  Components.init(flex, manager);
  TeamsFilters.init(flex, manager);
};
